/**
 * HTTP/API URL Helpers
 * Utilities for constructing API URLs for Unicity services
 */

// ============================================================================
// Constants
// ============================================================================

const MEMBER_CALLS_2 = 'member-calls2'
const MEMBER_CALLS_2_DEV = 'member-calls2-dev'
const MEMBER_CALLS_4 = 'member-calls4'
// Production hostnames that must ALWAYS resolve to the live backend, regardless of
// any baked VITE_API_ENV or runtime window.__API_ENV__ override. This is the durable
// lock (covers every URL builder via getGlobalApiEnv); the CI bake is belt-and-suspenders.
const ALWAYS_LIVE_HOSTS = ['u26.unicity.com', 'uenroll.unicity.com']
const UNICITY_DOMAIN = 'unicity.com'
const HYDRA_DOMAIN = 'unicity.net'

const LIVE = 'live'
const DEV = 'dev'

type Environment = typeof LIVE | typeof DEV

// ============================================================================
// Global Environment Config
// ============================================================================

// Type for Vite's import.meta.env
interface ViteImportMeta {
    env?: {
        VITE_API_ENV?: string
        PROD?: boolean
        DEV?: boolean
    }
}

/**
 * Get global API environment from various sources:
 * 1. Vite env variable (import.meta.env.VITE_API_ENV)
 * 2. Window global (window.__API_ENV__)
 * 3. Default to 'live'
 */
const getGlobalApiEnv = (): Environment => {
    // Production hostname always wins — no bake or override can flip prod to dev.
    if (typeof window !== 'undefined' && ALWAYS_LIVE_HOSTS.includes(window.location.hostname)) {
        return LIVE
    }

    // Try Vite environment variable first
    try {
        const meta = import.meta as ViteImportMeta
        if (meta?.env?.VITE_API_ENV) {
            const env = meta.env.VITE_API_ENV
            if (env === LIVE || env === DEV) {
                return env
            }
        }
    } catch {
        // import.meta not available in this environment
    }

    // Try window global (can be set by app at runtime)
    if (typeof window !== 'undefined' && (window as Window & { __API_ENV__?: string }).__API_ENV__) {
        const env = (window as Window & { __API_ENV__?: string }).__API_ENV__ as string
        if (env === LIVE || env === DEV) {
            return env
        }
    }

    // Default to live
    return LIVE
}

// ============================================================================
// Proxy Configuration
// ============================================================================

interface ProxyConfig {
    country: string
    useProxy: boolean
    subdomain: string
    domain: string
    env: Environment
    path: string
}

const COUNTRY_USE_PROXY: ProxyConfig[] = [
    {
        country: 'vietnam',
        useProxy: true,
        subdomain: 'vn',
        domain: UNICITY_DOMAIN,
        env: LIVE,
        path: '/ushop'
    }
]

// ============================================================================
// Types
// ============================================================================

export interface ProxyUrlOptions {
    /** Environment: 'live' or 'dev' */
    env?: Environment
    /** Country name (lowercase) */
    country?: string
    /** API path */
    path?: string
}

export interface HydraUrlOptions {
    /** Environment: 'live' or 'dev' */
    env?: Environment
    /** API path */
    path?: string
}

// ============================================================================
// URL Builders
// ============================================================================

/**
 * Builds URL for member-calls2 API
 * Handles proxy routing for specific countries
 *
 * @example
 * ```ts
 * memberCalls2Url({ path: '/settings/country' })
 * // Returns: "https://member-calls2.unicity.com/settings/country"
 *
 * memberCalls2Url({ env: 'dev', path: '/test' })
 * // Returns: "https://member-calls2-dev.unicity.com/test"
 * ```
 */
export const memberCalls2Url = ({ env, country, path = '' }: ProxyUrlOptions): string => {
    // Use provided env or fallback to global config
    const effectiveEnv = env ?? getGlobalApiEnv()
    const domain = typeof window !== 'undefined' ? window.location.hostname : ''

    // Check if country needs proxy
    const useProxy = COUNTRY_USE_PROXY.find(item => item.country === country)
    if (useProxy) {
        return `https://${useProxy.subdomain}.${domain}${useProxy.path}`
    }

    const subDomain = effectiveEnv === LIVE ? MEMBER_CALLS_2 : MEMBER_CALLS_2_DEV

    const baseUrl = `https://${subDomain}.${UNICITY_DOMAIN}${path}`

    // Append isQa=1 when env is DEV and path starts with /etlV2
    if (effectiveEnv === DEV && path.startsWith('/etlV2')) {
        const separator = path.includes('?') ? '&' : '?'
        return `${baseUrl}${separator}isQa=1`
    }

    return baseUrl
}

/**
 * Builds URL for member-calls4 API
 *
 * @example
 * ```ts
 * memberCalls4Url({})
 * // Returns: "https://member-calls4.unicity.com"
 * ```
 */
export const memberCalls4Url = ({ country }: ProxyUrlOptions): string => {
    const useProxy = COUNTRY_USE_PROXY.find(item => item.country === country)
    if (useProxy) {
        return `https://${useProxy.subdomain}.${UNICITY_DOMAIN}${useProxy.path}`
    }

    return `https://${MEMBER_CALLS_4}.${UNICITY_DOMAIN}`
}

/**
 * Builds URL for Hydra API
 *
 * @example
 * ```ts
 * hydraUrl({ path: 'customers/123' })
 * // Returns: "https://hydra.unicity.net/v5a/customers/123"
 *
 * hydraUrl({ env: 'dev', path: 'test' })
 * // Returns: "https://hydraqa.unicity.net/v5a-test/test"
 * ```
 */
export const hydraUrl = ({ env, path = '' }: HydraUrlOptions): string => {
    // Use provided env or fallback to global config
    const effectiveEnv = env ?? getGlobalApiEnv()
    const subdomain = effectiveEnv === LIVE ? 'hydra' : 'hydraqa'
    const version = effectiveEnv === LIVE ? 'v5a' : 'v5a-test'

    return `https://${subdomain}.${HYDRA_DOMAIN}/${version}/${path}`
}

/**
 * Returns current environment from global config
 * @returns 'live' or 'dev' based on VITE_API_ENV or window.__API_ENV__
 */
export const ENV = (): Environment => {
    return getGlobalApiEnv()
}

/**
 * Get current API environment
 * Alias for getGlobalApiEnv for external use
 */
export { getGlobalApiEnv }

// ============================================================================
// Axios Interceptors (if needed)
// ============================================================================

/**
 * Common headers for Unicity API calls
 */
export const getCommonHeaders = (token?: string) => ({
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    'x-application': 'asia.dsc-tools'
})
